import React, { useState, useEffect, useCallback } from 'react';
import { FileText, AlertCircle, Calendar, CheckCircle, Play, Loader, TrendingUp, Target, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { useApi } from '../hooks/useApi';

const Models = () => {
  const { fetchGet, fetchPost } = useApi();
  const [error, setError] = useState('');
  const [addOneHour, setAddOneHour] = useState(true);
  const [applyRidge, setApplyRidge] = useState(false);
  const [applyRisk, setApplyRisk] = useState(true);
  const [applySpikeRisk, setApplySpikeRisk] = useState(false);
  const [ridgeShifts, setRidgeShifts] = useState(null);
  const [riskShifts, setRiskShifts] = useState(null);
  const [riskScores, setRiskScores] = useState(null);
  const [spikeRiskShifts, setSpikeRiskShifts] = useState(null);
  const [spikeRiskScores, setSpikeRiskScores] = useState(null);
  const [predictDate, setPredictDate] = useState('');
  const [predicting, setPredicting] = useState(false);
  const [predictStatus, setPredictStatus] = useState(null);
  const [predictModelVersion, setPredictModelVersion] = useState('v2.0.0');
  const [predictFixIIModelVersion, setPredictFixIIModelVersion] = useState('v1.0.0-fix2');
  const [predictProbModelVersion, setPredictProbModelVersion] = useState('v3.1.0');
  const [fixIModelAvailable, setFixIModelAvailable] = useState(false);
  const [fixIIModelAvailable, setFixIIModelAvailable] = useState(false);
  const [probModelAvailable, setProbModelAvailable] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  // New state for prediction viewer
  const [viewerDate, setViewerDate] = useState('');
  const [viewerFixIModel, setViewerFixIModel] = useState('v2.0.0');
  const [viewerFixIIModel, setViewerFixIIModel] = useState('v1.0.0-fix2');
  const [viewerProbModel, setViewerProbModel] = useState('v3.1.0');
  const [viewerFixISpread, setViewerFixISpread] = useState(5);
  const [viewerRiskScoreModel, setViewerRiskScoreModel] = useState('prod-v2.0.0');
  const [viewerSpikeRiskModel, setViewerSpikeRiskModel] = useState('prod-spike-r3-champion');
  const [viewerLoading, setViewerLoading] = useState(false);
  const [fixIPredictions, setFixIPredictions] = useState(null);
  const [fixIIPredictions, setFixIIPredictions] = useState(null);
  const [probPredictions, setProbPredictions] = useState(null);

  const [meteoDe, setMeteoDe] = useState(null);
  const [eexLastTrade, setEexLastTrade] = useState(null);
  const [viewerIda1Model, setViewerIda1Model] = useState('ida1-v1.0.3');
  const [ida1Predictions, setIda1Predictions] = useState(null);

  // Fold/unfold states for prediction sections
  const [probExpanded, setProbExpanded] = useState(true);
  const [fixIExpanded, setFixIExpanded] = useState(true);
  const [fixIIExpanded, setFixIIExpanded] = useState(true);
  const [ida1Expanded, setIda1Expanded] = useState(true);


  // Available models for dropdowns
  const fixIModels = [
    'v1.0.0',
    'v1.0.1',
    'v1.0.2',
    'v1.0.3',
    'v1.0.3.1',
    'v2.0.0',
    'v2.0.0-cp14',
    'v2.0.0-hourly',
    'v2.0.0-l1',
    'v2.0.0-narx',
    'v2.0.0-narx-single'
  ];

  const fixIIModels = [
    'v1.0.0-fix2',
    'v1.0.0-fix2-cp14',
    'v2.0.0-l1-fix2',
    'v2.0.0-narx-fix2'
  ];

  // Set default predict date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    setPredictDate(tomorrowStr);
    setViewerDate(tomorrowStr);
  }, []);

  // Check model availability
  const checkModelAvailability = useCallback(async (modelVersion, date) => {
    if (!date || !modelVersion) return false;

    try {
      const response = await fetchGet(
        `/is_model_available_for?model_version=${encodeURIComponent(modelVersion)}&date=${encodeURIComponent(date)}`
      );
      const data = await response.json();
      return data.status === 'success' && data.available === true;
    } catch (err) {
      console.error(`Error checking model availability for ${modelVersion}:`, err);
      return false;
    }
  }, [fetchGet]);

  // Check probabilistic model availability
  const checkProbModelAvailability = useCallback(async (modelVersion, date) => {
    if (!date || !modelVersion) return false;

    try {
      const response = await fetchGet(
        `/is_prob_model_available_for?model_version=${encodeURIComponent(modelVersion)}&date=${encodeURIComponent(date)}`
      );
      const data = await response.json();
      return data.status === 'success' && data.available === true;
    } catch (err) {
      console.error(`Error checking probabilistic model availability for ${modelVersion}:`, err);
      return false;
    }
  }, [fetchGet]);

  // Check availability when date or model versions change
  useEffect(() => {
    const checkAvailability = async () => {
      if (!predictDate) return;

      setCheckingAvailability(true);
      try {
        const [fixIAvailable, fixIIAvailable, probAvailable] = await Promise.all([
          checkModelAvailability(predictModelVersion, predictDate),
          checkModelAvailability(predictFixIIModelVersion, predictDate),
          checkProbModelAvailability(predictProbModelVersion, predictDate)
        ]);

        setFixIModelAvailable(fixIAvailable);
        setFixIIModelAvailable(fixIIAvailable);
        setProbModelAvailable(probAvailable);
      } finally {
        setCheckingAvailability(false);
      }
    };

    checkAvailability();
  }, [
    checkModelAvailability,
    checkProbModelAvailability,
    predictDate,
    predictModelVersion,
    predictFixIIModelVersion,
    predictProbModelVersion
  ]);

  const triggerPrediction = async () => {
    if (!predictDate) {
      alert('Please select a date for prediction');
      return;
    }

    setPredicting(true);
    setPredictStatus(null);

    try {
      // Prepare form data for Fix I prediction
      const formDataFixI = new FormData();
      formDataFixI.append('date', predictDate);
      formDataFixI.append('model_version', predictModelVersion);

      // Prepare form data for Fix II prediction
      const formDataFixII = new FormData();
      formDataFixII.append('date', predictDate);
      formDataFixII.append('model_version', predictFixIIModelVersion);

      // Prepare form data for Probabilistic prediction
      const formDataProb = new FormData();
      formDataProb.append('date', predictDate);
      formDataProb.append('model_version', predictProbModelVersion);

      // Call all three endpoints in parallel
      const [responseFixI, responseFixII, responseProb] = await Promise.all([
        fetchPost('/predict', formDataFixI),
        fetchPost('/predict-fix2', formDataFixII),
        fetchPost('/predict-prob', formDataProb)
      ]);

      const dataFixI = await responseFixI.json();
      const dataFixII = await responseFixII.json();
      const dataProb = await responseProb.json();

      // Check if all succeeded
      if (dataFixI.status === 'success' && dataFixII.status === 'success' && dataProb.status === 'success') {
        setPredictStatus({
          type: 'success',
          message: `All prediction tasks started successfully for ${predictDate}`,
          details: [
            `Fix I: ${dataFixI.model_version} (Task: ${dataFixI.task_arn?.split('/').pop()})`,
            `Fix II: ${dataFixII.model_version} (Task: ${dataFixII.task_arn?.split('/').pop()})`,
            `Prob: ${dataProb.model_version} (Task: ${dataProb.task_arn?.split('/').pop()})`
          ]
        });
      } else {
        // Handle partial or complete failure
        const errors = [];
        if (dataFixI.status !== 'success') {
          errors.push(`Fix I: ${dataFixI.message || 'Failed'}`);
        }
        if (dataFixII.status !== 'success') {
          errors.push(`Fix II: ${dataFixII.message || 'Failed'}`);
        }
        if (dataProb.status !== 'success') {
          errors.push(`Prob: ${dataProb.message || 'Failed'}`);
        }

        setPredictStatus({
          type: 'error',
          message: 'One or more prediction tasks failed',
          details: errors
        });
      }
    } catch (err) {
      setPredictStatus({
        type: 'error',
        message: 'Network error. Failed to trigger predictions.'
      });
      console.error('Error triggering predictions:', err);
    } finally {
      setPredicting(false);
    }
  };

  // Fetch predictions for viewer (single combined API call)
  const fetchPredictionsForViewer = async () => {
    if (!viewerDate) {
      alert('Please select a date');
      return;
    }

    setViewerLoading(true);
    setError('');
    setFixIPredictions(null);
    setFixIIPredictions(null);
    setProbPredictions(null);
    setRidgeShifts(null);
    setRiskShifts(null);
    setRiskScores(null);
    setSpikeRiskShifts(null);
    setSpikeRiskScores(null);
    setMeteoDe(null);
    setEexLastTrade(null);
    setIda1Predictions(null);

    try {
      const spikeParam = applySpikeRisk && viewerSpikeRiskModel
        ? `&spike_risk_model=${encodeURIComponent(viewerSpikeRiskModel)}`
        : '';
      const response = await fetchGet(
        `/viewer-data/${viewerDate}?fix_i_model=${encodeURIComponent(viewerFixIModel)}&fix_ii_model=${encodeURIComponent(viewerFixIIModel)}&prob_model=${encodeURIComponent(viewerProbModel)}&ida1_model=${encodeURIComponent(viewerIda1Model)}&risk_model=${encodeURIComponent(viewerRiskScoreModel)}${spikeParam}`
      );
      const data = await response.json();

      if (data.status === 'success') {
        // Ridge shifts
        if (data.ridge && data.ridge.status === 'success' && data.ridge.shifts) {
          setRidgeShifts(data.ridge.shifts);
        }

        // Risk shifts (collapse)
        if (data.risk_shift && data.risk_shift.status === 'success' && data.risk_shift.shifts) {
          setRiskShifts(data.risk_shift.shifts);
        }

        // Risk scores (collapse, raw from predictions.risk_score.risk_score)
        if (data.risk_score && data.risk_score.status === 'success' && data.risk_score.scores) {
          setRiskScores(data.risk_score.scores);
        }

        // Spike risk shifts
        if (data.spike_risk_shift && data.spike_risk_shift.status === 'success' && data.spike_risk_shift.shifts) {
          setSpikeRiskShifts(data.spike_risk_shift.shifts);
        }

        // Spike risk scores (raw)
        if (data.spike_risk_score && data.spike_risk_score.status === 'success' && data.spike_risk_score.scores) {
          setSpikeRiskScores(data.spike_risk_score.scores);
        }

        // Fix I
        if (data.fix_i && data.fix_i.status === 'success') {
          setFixIPredictions(data.fix_i);
        } else {
          setError(`Fix I: ${data.fix_i?.message || 'Failed to load predictions'}`);
        }

        // Fix II
        if (data.fix_ii && data.fix_ii.status === 'success') {
          setFixIIPredictions(data.fix_ii);
        } else {
          setError(prev => prev ? `${prev}, Fix II: ${data.fix_ii?.message}` : `Fix II: ${data.fix_ii?.message || 'Failed to load predictions'}`);
        }

        // Prob
        if (data.prob && data.prob.status === 'success') {
          setProbPredictions(data.prob);
        } else {
          setError(prev => prev ? `${prev}, Prob: ${data.prob?.message}` : `Prob: ${data.prob?.message || 'Failed to load predictions'}`);
        }

        // Meteo DE
        if (data.meteo_de) {
          setMeteoDe(data.meteo_de);
        }

        // EEX last trade before 9:30 Warsaw
        if (data.eex_last_trade) {
          setEexLastTrade(data.eex_last_trade);
        }

        // IDA1
        if (data.ida1 && data.ida1.status === 'success') {
          setIda1Predictions(data.ida1);
        }
      } else {
        setError(data.message || 'Failed to fetch viewer data');
      }
    } catch (err) {
      setError('Network error. Failed to fetch predictions.');
      console.error('Error fetching predictions:', err);
    } finally {
      setViewerLoading(false);
    }
  };

  const calculateDailyAverages = (csvContent) => {
    if (!csvContent || csvContent === 'Predictions not available yet.') {
      return null;
    }

    const lines = csvContent.trim().split('\n');
    if (lines.length <= 1) return null;

    const headers = lines[0].split(',');
    const predictionIndex = headers.indexOf('prediction');
    const meteologicaIndex = headers.indexOf('meteologica_price_pl');
    const fullEnsembleIndex = headers.indexOf('prediction_full_ensemble');
    const threeMonthEnsembleIndex = headers.indexOf('prediction_3month_ensemble');
    const hourIndex = headers.indexOf('hour');

    if (predictionIndex === -1 || meteologicaIndex === -1) return null;

    const rows = lines.slice(1).map(line => line.split(','));

    let predictionSum = 0;
    let meteologicaSum = 0;
    let fullEnsembleSum = 0;
    let threeMonthEnsembleSum = 0;
    let count = 0;
    let fullEnsembleCount = 0;
    let threeMonthEnsembleCount = 0;

    rows.forEach(row => {
      const hour = hourIndex >= 0 ? parseInt(row[hourIndex]) : null;
      const shift = (applyRidge && ridgeShifts && hour !== null) ? (ridgeShifts[hour] || 0) : 0;
      const predValue = parseFloat(row[predictionIndex]) + shift;
      const meteoValue = parseFloat(row[meteologicaIndex]);

      if (!isNaN(predValue) && !isNaN(meteoValue)) {
        predictionSum += predValue;
        meteologicaSum += meteoValue;
        count++;
      }

      // Calculate full ensemble average if column exists
      if (fullEnsembleIndex !== -1) {
        const fullEnsembleValue = parseFloat(row[fullEnsembleIndex]) + shift;
        if (!isNaN(fullEnsembleValue)) {
          fullEnsembleSum += fullEnsembleValue;
          fullEnsembleCount++;
        }
      }

      // Calculate 3-month ensemble average if column exists
      if (threeMonthEnsembleIndex !== -1) {
        const threeMonthValue = parseFloat(row[threeMonthEnsembleIndex]) + shift;
        if (!isNaN(threeMonthValue)) {
          threeMonthEnsembleSum += threeMonthValue;
          threeMonthEnsembleCount++;
        }
      }
    });

    if (count === 0) return null;

    const result = {
      predictionAvg: (predictionSum / count).toFixed(2),
      meteologicaAvg: (meteologicaSum / count).toFixed(2)
    };

    // Add full ensemble average if data exists
    if (fullEnsembleCount > 0) {
      result.fullEnsembleAvg = (fullEnsembleSum / fullEnsembleCount).toFixed(2);
    }

    // Add 3-month ensemble average if data exists
    if (threeMonthEnsembleCount > 0) {
      result.threeMonthEnsembleAvg = (threeMonthEnsembleSum / threeMonthEnsembleCount).toFixed(2);
    }

    return result;
  };

  const formatCSVAsTable = (csvContent, modelType) => {
    if (!csvContent || csvContent === 'Predictions not available yet.') {
      return (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Predictions not available for {modelType}</p>
        </div>
      );
    }

    const lines = csvContent.trim().split('\n');
    if (lines.length === 0) return null;

    const headers = lines[0].split(',');
    const rows = lines.slice(1).map(line => line.split(','));

    // Apply ridge shift to prediction columns if enabled
    const hourIdx = headers.indexOf('hour');
    const predCols = ['prediction', 'prediction_full_ensemble', 'prediction_3month_ensemble']
      .map(h => headers.indexOf(h)).filter(i => i >= 0);
    if (applyRidge && ridgeShifts && hourIdx >= 0) {
      rows.forEach(row => {
        const hour = parseInt(row[hourIdx]);
        const shift = ridgeShifts[hour];
        if (shift !== undefined && shift !== null) {
          predCols.forEach(ci => {
            const val = parseFloat(row[ci]);
            if (!isNaN(val)) {
              row[ci] = String(val + shift);
            }
          });
        }
      });
    }

    // Define which columns should be displayed
    const displayColumns = ['date', 'hour', 'prediction', 'prediction_full_ensemble', 'prediction_3month_ensemble'];
    const displayHeaders = headers.filter(header => displayColumns.includes(header));
    const displayIndices = displayHeaders.map(header => headers.indexOf(header));

    // Format header names for display
    const formatHeaderName = (header) => {
      const headerMap = {
        'prediction': 'Prediction',
        'prediction_full_ensemble': 'Full Ensemble',
        'prediction_3month_ensemble': '3-Month Ensemble',
        'date': 'Date',
        'hour': 'Hour'
      };
      return headerMap[header] || header;
    };

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {displayHeaders.map((header, index) => (
                <th
                  key={index}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {formatHeaderName(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}>
                {displayIndices.map((colIndex, displayIndex) => {
                  const header = displayHeaders[displayIndex];
                  const cell = row[colIndex];

                  // Apply hour adjustment if it's the hour column and addOneHour is true
                  if (header === 'hour' && addOneHour && !isNaN(cell)) {
                    const hourValue = parseInt(cell) + 1;
                    return (
                      <td key={displayIndex} className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">
                        {hourValue}
                      </td>
                    );
                  }

                  // Format numeric values (except date and hour)
                  if (header !== 'date' && header !== 'hour' && !isNaN(cell) && cell !== '') {
                    return (
                      <td key={displayIndex} className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">
                        {parseFloat(cell).toFixed(2)}
                      </td>
                    );
                  }

                  return (
                    <td key={displayIndex} className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">
                      {cell}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const calculateProbAverages = (csvContent) => {
    if (!csvContent || csvContent === 'Predictions not available yet.') {
      return null;
    }

    const lines = csvContent.trim().split('\n');
    if (lines.length <= 1) return null;

    const headers = lines[0].split(',');
    const stackedQ50Index = headers.indexOf('stacked_q50');
    const meteologicaIndex = headers.indexOf('meteologica_price_pl');
    const fullQ50Index = headers.indexOf('full_q50');
    const threeMonthQ50Index = headers.indexOf('3month_q50');
    const hourIdx = headers.indexOf('hour');

    if (stackedQ50Index === -1) return null;

    const rows = lines.slice(1).map(line => line.split(','));

    let stackedQ50Sum = 0;
    let meteologicaSum = 0;
    let fullQ50Sum = 0;
    let threeMonthQ50Sum = 0;
    let count = 0;
    let meteologicaCount = 0;
    let fullQ50Count = 0;
    let threeMonthQ50Count = 0;

    rows.forEach(row => {
      const hour = hourIdx >= 0 ? parseInt(row[hourIdx]) : null;
      const ridgeShift = (applyRidge && ridgeShifts && hour !== null) ? (ridgeShifts[hour] || 0) : 0;
      const riskShift = (applyRisk && riskShifts && hour !== null) ? (riskShifts[hour] || 0) : 0;
      const spikeShift = (applySpikeRisk && spikeRiskShifts && hour !== null) ? (spikeRiskShifts[hour] || 0) : 0;
      const shift = ridgeShift + riskShift + spikeShift;

      const stackedQ50Value = parseFloat(row[stackedQ50Index]) + shift;
      if (!isNaN(stackedQ50Value)) {
        stackedQ50Sum += stackedQ50Value;
        count++;
      }

      if (meteologicaIndex !== -1) {
        const meteoValue = parseFloat(row[meteologicaIndex]);
        if (!isNaN(meteoValue)) {
          meteologicaSum += meteoValue;
          meteologicaCount++;
        }
      }

      if (fullQ50Index !== -1) {
        const fullQ50Value = parseFloat(row[fullQ50Index]) + shift;
        if (!isNaN(fullQ50Value)) {
          fullQ50Sum += fullQ50Value;
          fullQ50Count++;
        }
      }

      if (threeMonthQ50Index !== -1) {
        const threeMonthQ50Value = parseFloat(row[threeMonthQ50Index]) + shift;
        if (!isNaN(threeMonthQ50Value)) {
          threeMonthQ50Sum += threeMonthQ50Value;
          threeMonthQ50Count++;
        }
      }
    });

    if (count === 0) return null;

    const result = {
      predictionAvg: (stackedQ50Sum / count).toFixed(2)
    };

    if (meteologicaCount > 0) {
      result.meteologicaAvg = (meteologicaSum / meteologicaCount).toFixed(2);
    }

    if (fullQ50Count > 0) {
      result.fullEnsembleAvg = (fullQ50Sum / fullQ50Count).toFixed(2);
    }

    if (threeMonthQ50Count > 0) {
      result.threeMonthEnsembleAvg = (threeMonthQ50Sum / threeMonthQ50Count).toFixed(2);
    }

    return result;
  };

  const formatProbCSVAsTable = (csvContent) => {
    if (!csvContent || csvContent === 'Predictions not available yet.') {
      return (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Probabilistic predictions not available</p>
        </div>
      );
    }

    const lines = csvContent.trim().split('\n');
    if (lines.length === 0) return null;

    const headers = lines[0].split(',');
    const rows = lines.slice(1).map(line => line.split(','));

    // Apply ridge + collapse risk + spike risk shift to quantile columns if enabled
    const hourColIdx = headers.indexOf('hour');
    const quantileCols = headers.map((h, i) => h.startsWith('stacked_q') ? i : -1).filter(i => i >= 0);
    if (hourColIdx >= 0 && ((applyRidge && ridgeShifts) || (applyRisk && riskShifts) || (applySpikeRisk && spikeRiskShifts))) {
      rows.forEach(row => {
        const hour = parseInt(row[hourColIdx]);
        const ridgeShift = (applyRidge && ridgeShifts && ridgeShifts[hour] != null) ? ridgeShifts[hour] : 0;
        const riskShift = (applyRisk && riskShifts && riskShifts[hour] != null) ? riskShifts[hour] : 0;
        const spikeShift = (applySpikeRisk && spikeRiskShifts && spikeRiskShifts[hour] != null) ? spikeRiskShifts[hour] : 0;
        const shift = ridgeShift + riskShift + spikeShift;
        if (shift !== 0) {
          quantileCols.forEach(ci => {
            const val = parseFloat(row[ci]);
            if (!isNaN(val)) {
              row[ci] = String(val + shift);
            }
          });
        }
      });
    }

    // Define which columns should be displayed (hour + all stacked quantiles — Date is omitted)
    const stackedColumns = ['hour', 'stacked_q05', 'stacked_q10', 'stacked_q20', 'stacked_q30', 'stacked_q40', 'stacked_q50', 'stacked_q60', 'stacked_q70', 'stacked_q80', 'stacked_q90', 'stacked_q95'];
    const displayHeaders = headers.filter(header => stackedColumns.includes(header));
    const displayIndices = displayHeaders.map(header => headers.indexOf(header));
    const showRiskCol = applyRisk;
    const showSpikeCol = applySpikeRisk;

    // Format header names for display (remove stacked_ prefix)
    const formatHeaderName = (header) => {
      if (header.startsWith('stacked_')) {
        return header.replace('stacked_', '');
      }
      if (header === 'date') return 'Date';
      if (header === 'hour') return 'Hour';
      return header;
    };

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {displayHeaders.map((header, index) => {
                const isQ50 = header === 'stacked_q50';
                return (
                  <th
                    key={index}
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isQ50 ? 'font-bold text-gray-700' : ''}`}
                  >
                    {formatHeaderName(header)}
                  </th>
                );
              })}
              {showRiskCol && (
                <>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Down Shift
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Down Risk
                  </th>
                </>
              )}
              {showSpikeCol && (
                <>
                  <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                    Up Shift
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                    Up Risk
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, rowIndex) => {
              const rowHour = hourColIdx >= 0 ? parseInt(row[hourColIdx]) : null;
              const shiftVal = (showRiskCol && riskShifts && rowHour !== null && riskShifts[rowHour] != null)
                ? riskShifts[rowHour]
                : null;
              const riskVal = (showRiskCol && riskScores && rowHour !== null && riskScores[rowHour] != null)
                ? riskScores[rowHour]
                : null;
              const spikeShiftVal = (showSpikeCol && spikeRiskShifts && rowHour !== null && spikeRiskShifts[rowHour] != null)
                ? spikeRiskShifts[rowHour]
                : null;
              const spikeRiskVal = (showSpikeCol && spikeRiskScores && rowHour !== null && spikeRiskScores[rowHour] != null)
                ? spikeRiskScores[rowHour]
                : null;
              return (
                <tr key={rowIndex} className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-purple-50`}>
                  {displayIndices.map((colIndex, displayIndex) => {
                    const header = displayHeaders[displayIndex];
                    const cell = row[colIndex];

                    // Apply hour adjustment if it's the hour column and addOneHour is true
                    if (header === 'hour' && addOneHour && !isNaN(cell)) {
                      const hourValue = parseInt(cell) + 1;
                      return (
                        <td key={displayIndex} className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">
                          {hourValue}
                        </td>
                      );
                    }

                    // Format numeric values (except date and hour)
                    if (header !== 'date' && header !== 'hour' && !isNaN(cell) && cell !== '') {
                      const isQ50 = header === 'stacked_q50';
                      return (
                        <td key={displayIndex} className={`px-3 py-1.5 whitespace-nowrap text-sm text-gray-900 ${isQ50 ? 'font-bold' : ''}`}>
                          {parseFloat(cell).toFixed(2)}
                        </td>
                      );
                    }

                    return (
                      <td key={displayIndex} className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">
                        {cell}
                      </td>
                    );
                  })}
                  {showRiskCol && (
                    <>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-sm ${shiftVal != null && shiftVal < 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                        {shiftVal != null ? shiftVal.toFixed(2) : '-'}
                      </td>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-sm ${riskVal != null && riskVal < 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                        {riskVal != null ? riskVal.toFixed(2) : '-'}
                      </td>
                    </>
                  )}
                  {showSpikeCol && (
                    <>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-sm ${spikeShiftVal != null && spikeShiftVal > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                        {spikeShiftVal != null ? spikeShiftVal.toFixed(2) : '-'}
                      </td>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-sm ${spikeRiskVal != null && spikeRiskVal > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                        {spikeRiskVal != null ? spikeRiskVal.toFixed(2) : '-'}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Run Prediction Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Run Prediction</h2>
        <p className="text-gray-600 mt-1">Generate new predictions using selected models</p>
      </div>

      {/* Prediction Trigger Section - Blue Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-end space-x-4">
            <div>
              <label htmlFor="predict-date" className="block text-sm font-medium text-gray-700 mb-1">
                Predict for date:
              </label>
              <input
                id="predict-date"
                type="date"
                value={predictDate}
                onChange={(e) => setPredictDate(e.target.value)}
                disabled={predicting}
                className="w-44 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="predict-model-version" className="block text-sm font-medium text-gray-700 mb-1">
                Fix I Model Version:
              </label>
              <div className="flex items-center space-x-2">
                <select
                  id="predict-model-version"
                  value={predictModelVersion}
                  onChange={(e) => setPredictModelVersion(e.target.value)}
                  disabled={predicting}
                  className="w-44 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-[42px]"
                >
                  <option value="v1.0.0">v1.0.0</option>
                  <option value="v2.0.0">v2.0.0</option>
                </select>
                {checkingAvailability ? (
                  <Loader className="w-3 h-3 animate-spin text-gray-400" />
                ) : (
                  <div className={`w-3 h-3 rounded-full ${fixIModelAvailable ? 'bg-green-500' : 'bg-red-500'}`} />
                )}
              </div>
            </div>
            <div>
              <label htmlFor="predict-fix-ii-model-version" className="block text-sm font-medium text-gray-700 mb-1">
                Fix II Model Version:
              </label>
              <div className="flex items-center space-x-2">
                <select
                  id="predict-fix-ii-model-version"
                  value={predictFixIIModelVersion}
                  onChange={(e) => setPredictFixIIModelVersion(e.target.value)}
                  disabled={predicting}
                  className="w-44 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-[42px]"
                >
                  <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                  <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
                </select>
                {checkingAvailability ? (
                  <Loader className="w-3 h-3 animate-spin text-gray-400" />
                ) : (
                  <div className={`w-3 h-3 rounded-full ${fixIIModelAvailable ? 'bg-green-500' : 'bg-red-500'}`} />
                )}
              </div>
            </div>
            <div>
              <label htmlFor="predict-prob-model-version" className="block text-sm font-medium text-gray-700 mb-1">
                Probabilistic Model Version:
              </label>
              <div className="flex items-center space-x-2">
                <select
                  id="predict-prob-model-version"
                  value={predictProbModelVersion}
                  onChange={(e) => setPredictProbModelVersion(e.target.value)}
                  disabled={predicting}
                  className="w-44 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-[42px]"
                >
                  <option value="v3.1.0">v3.1.0</option>
                </select>
                {checkingAvailability ? (
                  <Loader className="w-3 h-3 animate-spin text-gray-400" />
                ) : (
                  <div className={`w-3 h-3 rounded-full ${probModelAvailable ? 'bg-green-500' : 'bg-red-500'}`} />
                )}
              </div>
            </div>
            <button
              onClick={triggerPrediction}
              disabled={predicting || !predictDate || !fixIModelAvailable || !fixIIModelAvailable || !probModelAvailable || checkingAvailability}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-md font-medium transition-colors h-[42px] ${
                predicting || !fixIModelAvailable || !fixIIModelAvailable || !probModelAvailable || checkingAvailability
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              title={!fixIModelAvailable || !fixIIModelAvailable || !probModelAvailable ? 'Models not available for selected date' : ''}
            >
              {predicting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Starting task...</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span>Run Prediction</span>
                </>
              )}
            </button>
          </div>

          {/* Status Message */}
          {predictStatus && (
            <div className={`flex items-start space-x-2 ${
              predictStatus.type === 'success' ? 'text-green-700' : 'text-red-700'
            }`}>
              {predictStatus.type === 'success' ? (
                <CheckCircle className="w-5 h-5 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 mt-0.5" />
              )}
              <div>
                <p className="text-sm font-medium">{predictStatus.message}</p>
                {predictStatus.taskArn && (
                  <p className="text-xs mt-1">Task ARN: {predictStatus.taskArn.split('/').pop()}</p>
                )}
                {predictStatus.taskDefinition && (
                  <p className="text-xs mt-0.5">Task Definition: {predictStatus.taskDefinition}</p>
                )}
                {predictStatus.details && (
                  <ul className="text-xs mt-1">
                    {predictStatus.details.map((detail, idx) => (
                      <li key={idx}>• {detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Model Predictions Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Model Predictions</h2>
        <p className="text-gray-600 mt-1">View predictions from different model versions</p>
      </div>

      {/* Prediction Viewer Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">View Predictions</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Date Input */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar size={16} />
              <span>Date</span>
            </label>
            <input
              type="date"
              value={viewerDate}
              onChange={(e) => setViewerDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Fix I Model Dropdown */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <TrendingUp size={16} />
              <span>Fix I Model</span>
            </label>
            <select
              value={viewerFixIModel}
              onChange={(e) => setViewerFixIModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {fixIModels.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>

          {/* Fix II Model Dropdown */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <TrendingUp size={16} />
              <span>Fix II Model</span>
            </label>
            <select
              value={viewerFixIIModel}
              onChange={(e) => setViewerFixIIModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {fixIIModels.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>

          {/* Probabilistic Model Dropdown */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <TrendingUp size={16} />
              <span>Probabilistic Model</span>
            </label>
            <select
              value={viewerProbModel}
              onChange={(e) => setViewerProbModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="v3.1.0">v3.1.0</option>
            </select>
          </div>

          {/* IDA1 Model Dropdown */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <TrendingUp size={16} />
              <span>IDA1 Model</span>
            </label>
            <select
              value={viewerIda1Model}
              onChange={(e) => setViewerIda1Model(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ida1-v1.0.3">ida1-v1.0.3</option>
            </select>
          </div>

          {/* Fix I Model Spread */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <Target size={16} />
              <span>Fix I Spread</span>
            </label>
            <input
              type="number"
              value={viewerFixISpread}
              onChange={(e) => setViewerFixISpread(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Price Collapse Risk Engine */}
          <div>
            <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${!applyRisk ? 'text-gray-400' : 'text-gray-700'}`}>
              <AlertCircle size={16} />
              <span>Price Collapse Risk Engine</span>
            </label>
            <select
              value={viewerRiskScoreModel}
              onChange={(e) => setViewerRiskScoreModel(e.target.value)}
              disabled={!applyRisk}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!applyRisk ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
              title={!applyRisk ? 'Enable Apply Price Collapse Risk to choose a Price Collapse Risk Engine' : ''}
            >
              <option value="prod-v2.0.0">prod-v2.0.0</option>
              <option value="collapse_logreg_v1_wf_pse_till_500">collapse_logreg_v1_wf_pse_till_500</option>
            </select>
          </div>

          {/* Price Spike Risk Engine */}
          <div>
            <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${!applySpikeRisk ? 'text-gray-400' : 'text-gray-700'}`}>
              <AlertCircle size={16} />
              <span>Price Spike Risk Engine</span>
            </label>
            <select
              value={viewerSpikeRiskModel}
              onChange={(e) => setViewerSpikeRiskModel(e.target.value)}
              disabled={!applySpikeRisk}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!applySpikeRisk ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
              title={!applySpikeRisk ? 'Enable Apply Price Spike Risk to choose a Price Spike Risk Engine' : ''}
            >
              <option value="prod-spike-v2.0.0">prod-spike-v2.0.0</option>
              <option value="prod-spike-ra-pure">prod-spike-ra-pure</option>
              <option value="prod-spike-rz-champion">prod-spike-rz-champion</option>
              <option value="prod-spike-r3-champion">prod-spike-r3-champion</option>
            </select>
          </div>
        </div>

        {/* Show Predictions Button - Full Width */}
        <button
          onClick={fetchPredictionsForViewer}
          disabled={viewerLoading || !viewerDate}
          className={`w-full flex items-center justify-center space-x-2 px-6 py-2.5 rounded-md font-medium transition-colors ${
            viewerLoading || !viewerDate
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {viewerLoading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Loading...</span>
            </>
          ) : (
            <>
              <ChevronRight className="w-4 h-4" />
              <span>Show Predictions</span>
            </>
          )}
        </button>

        {/* Toggle switches */}
        <div className="mt-4 flex items-center space-x-6">
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-700">Apply Shift</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={applyRidge}
                onChange={(e) => setApplyRidge(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:border-blue-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-700">Apply Price Collapse Risk</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={applyRisk}
                onChange={(e) => setApplyRisk(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:border-blue-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-700">Apply Price Spike Risk</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={applySpikeRisk}
                onChange={(e) => setApplySpikeRisk(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:border-amber-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
            </label>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={addOneHour}
                onChange={(e) => setAddOneHour(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:border-blue-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error loading predictions</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Predictions Display */}
        {(fixIPredictions || fixIIPredictions || probPredictions) && (
          <div className="mt-6 space-y-4">
            {/* Probabilistic Predictions - Foldable, unfolded by default */}
            <div className="border border-gray-200 rounded-lg">
              <button
                onClick={() => setProbExpanded(!probExpanded)}
                className="w-full bg-purple-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-purple-100 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <h4 className="font-semibold text-gray-800">Probabilistic Predictions</h4>
                  {probPredictions && (
                    <span className="text-xs text-gray-600">(Model: {viewerProbModel})</span>
                  )}
                  {(() => {
                    if (!ridgeShifts || Object.keys(ridgeShifts).length === 0) {
                      return <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">Shift: N/A</span>;
                    }
                    const shiftValues = Object.values(ridgeShifts);
                    const avgShift = shiftValues.reduce((a, b) => a + b, 0) / shiftValues.length;
                    const bg = avgShift > 0 ? 'bg-green-100 text-green-800' : avgShift < 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-500';
                    return (
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${bg}`}>
                        Shift: {avgShift > 0 ? '+' : ''}{avgShift.toFixed(2)}{applyRidge ? '' : ' (off)'}
                      </span>
                    );
                  })()}
                  {applyRisk && (!riskShifts || Object.keys(riskShifts).length === 0) && (
                    <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">No Risk Score</span>
                  )}
                </div>
                {probExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
              </button>

              {probExpanded && (
                <div>
                  {probPredictions && probPredictions.content ? (
                    <div>
                      {/* Buy/Sell Prices and Averages for Probabilistic */}
                      {(() => {
                        const averages = calculateProbAverages(probPredictions.content);
                        const fixIAverages = fixIPredictions && fixIPredictions.content ? calculateDailyAverages(fixIPredictions.content) : null;
                        const fixIIAverages = fixIIPredictions && fixIIPredictions.content ? calculateDailyAverages(fixIIPredictions.content) : null;

                        if (!averages) return null;

                        const predictionAvg = parseFloat(averages.predictionAvg);
                        const spread = parseFloat(viewerFixISpread);
                        const buyPrice = (predictionAvg - spread).toFixed(2);
                        const sellPrice = (predictionAvg + spread).toFixed(2);
                        const shiftUnavailable = applyRidge && (!ridgeShifts || Object.keys(ridgeShifts).length === 0);

                        // Risk management based on Fix I vs Fix II comparison
                        let predsDiff = null;
                        let skipBuy = false;
                        let skipSell = false;

                        const effectiveCutoff = 1000;
                        if (fixIAverages && fixIIAverages) {
                          const fixIPredAvg = parseFloat(fixIAverages.predictionAvg);
                          const fixIIPredAvg = parseFloat(fixIIAverages.predictionAvg);
                          if (fixIIPredAvg !== 0) {
                            predsDiff = ((fixIPredAvg - fixIIPredAvg) / fixIIPredAvg) * 100;
                            // Skip buy if Fix I > Fix II by more than cutoff
                            if (predsDiff > effectiveCutoff) {
                              skipBuy = true;
                            }
                            // Skip sell if Fix II > Fix I by more than cutoff (negative diff)
                            if (predsDiff < -effectiveCutoff) {
                              skipSell = true;
                            }
                          }
                        }

                        return (
                          <div className="p-4 bg-gray-50 border-b border-gray-200">
                            {/* Buy/Sell Price + Avg Meteo DE + EEX Last Trade Row */}
                            <div className="grid grid-cols-4 gap-3 mb-3">
                              {shiftUnavailable ? (
                                <div className="bg-yellow-50 rounded p-3 border border-yellow-300">
                                  <p className="text-xs text-yellow-700 font-medium uppercase">Buy Price</p>
                                  <p className="text-sm text-yellow-600 mt-1">Ridge shift not available</p>
                                </div>
                              ) : skipBuy ? (
                                <div className="bg-red-50 rounded p-3 border border-red-300">
                                  <p className="text-xs text-red-600 font-medium uppercase">Buy Price - Skipped</p>
                                  <p className="text-xs text-red-500 mt-1">Fix I vs Fix II diff: {predsDiff?.toFixed(2)}%</p>
                                </div>
                              ) : (
                                <div className="bg-white rounded p-3 border border-green-300">
                                  <p className="text-xs text-gray-500 uppercase">Buy Price</p>
                                  <p className="text-xl font-bold text-green-600">{buyPrice}</p>
                                </div>
                              )}
                              {shiftUnavailable ? (
                                <div className="bg-yellow-50 rounded p-3 border border-yellow-300">
                                  <p className="text-xs text-yellow-700 font-medium uppercase">Sell Price</p>
                                  <p className="text-sm text-yellow-600 mt-1">Ridge shift not available</p>
                                </div>
                              ) : skipSell ? (
                                <div className="bg-red-50 rounded p-3 border border-red-300">
                                  <p className="text-xs text-red-600 font-medium uppercase">Sell Price - Skipped</p>
                                  <p className="text-xs text-red-500 mt-1">Fix I vs Fix II diff: {predsDiff?.toFixed(2)}%</p>
                                </div>
                              ) : (
                                <div className="bg-white rounded p-3 border border-red-300">
                                  <p className="text-xs text-gray-500 uppercase">Sell Price</p>
                                  <p className="text-xl font-bold text-red-600">{sellPrice}</p>
                                </div>
                              )}
                              <div className="bg-white rounded p-3 border border-blue-300">
                                <p className="text-xs text-gray-500 uppercase">Avg Meteo DE</p>
                                {meteoDe ? (
                                  <div>
                                    <p className="text-xl font-bold text-blue-600">{meteoDe.avg_eur} <span className="text-sm font-normal">EUR</span></p>
                                    {meteoDe.avg_pln != null && (
                                      <p className="text-sm text-gray-600">{meteoDe.avg_pln} <span className="text-xs">PLN</span></p>
                                    )}
                                    {meteoDe.eur_pln_rate && (
                                      <p className="text-xs text-gray-400 mt-1">Rate: {meteoDe.eur_pln_rate}</p>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-400 mt-1">N/A</p>
                                )}
                              </div>
                              <div className="bg-white rounded p-3 border border-amber-300">
                                <p className="text-xs text-gray-500 uppercase">EEX Last Trade Before 9:30</p>
                                {eexLastTrade ? (
                                  <div>
                                    <p className="text-xl font-bold text-amber-600">{eexLastTrade.eex_last_eur.toFixed(1)} <span className="text-sm font-normal">EUR</span></p>
                                    {eexLastTrade.eex_last_pln != null && (
                                      <p className="text-sm text-gray-600">{eexLastTrade.eex_last_pln.toFixed(1)} <span className="text-xs">PLN</span></p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">
                                      Rolling offset: {eexLastTrade.rolling_offset_used}
                                      {eexLastTrade.eur_pln_rate ? ` · Rate: ${eexLastTrade.eur_pln_rate}` : ''}
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-400 mt-1">N/A</p>
                                )}
                              </div>
                            </div>
                            {/* Averages Row */}
                            <div className="grid grid-cols-4 gap-3">
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-xs text-gray-500 uppercase">Prediction Avg (q50)</p>
                                <p className="text-lg font-bold text-purple-600">{averages.predictionAvg}</p>
                              </div>
                              {averages.meteologicaAvg && (
                                <div className="bg-white rounded p-2 border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Meteologica Avg</p>
                                  <p className="text-lg font-bold text-gray-600">{averages.meteologicaAvg}</p>
                                </div>
                              )}
                              {averages.fullEnsembleAvg && (
                                <div className="bg-white rounded p-2 border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Full Ensemble (q50)</p>
                                  <p className="text-lg font-bold text-purple-600">{averages.fullEnsembleAvg}</p>
                                </div>
                              )}
                              {averages.threeMonthEnsembleAvg && (
                                <div className="bg-white rounded p-2 border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">3-Month Ensemble (q50)</p>
                                  <p className="text-lg font-bold text-teal-600">{averages.threeMonthEnsembleAvg}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      <div className="p-4">
                        {formatProbCSVAsTable(probPredictions.content)}
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <p>No probabilistic predictions loaded</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Fix I and Fix II in a grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Fix I Predictions - Foldable, folded by default */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => setFixIExpanded(!fixIExpanded)}
                  className="w-full bg-blue-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <h4 className="font-semibold text-gray-800">Fix I Predictions</h4>
                    {fixIPredictions && (
                      <span className="text-xs text-gray-600">(Model: {viewerFixIModel})</span>
                    )}
                  </div>
                  {fixIExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                </button>

                {fixIExpanded && (
                  <div>
                    {fixIPredictions && fixIPredictions.content ? (
                      <div>
                        {(() => {
                          const averages = calculateDailyAverages(fixIPredictions.content);
                          return averages ? (
                            <div className="p-4 bg-gray-50 border-b border-gray-200">
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white rounded p-2 border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Prediction Avg</p>
                                  <p className="text-lg font-bold text-blue-600">{averages.predictionAvg}</p>
                                </div>
                                <div className="bg-white rounded p-2 border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Meteologica Avg</p>
                                  <p className="text-lg font-bold text-gray-600">{averages.meteologicaAvg}</p>
                                </div>
                                {averages.fullEnsembleAvg && (
                                  <div className="bg-white rounded p-2 border border-gray-200">
                                    <p className="text-xs text-gray-500 uppercase">Full Ensemble</p>
                                    <p className="text-lg font-bold text-purple-600">{averages.fullEnsembleAvg}</p>
                                  </div>
                                )}
                                {averages.threeMonthEnsembleAvg && (
                                  <div className="bg-white rounded p-2 border border-gray-200">
                                    <p className="text-xs text-gray-500 uppercase">3-Month Ensemble</p>
                                    <p className="text-lg font-bold text-teal-600">{averages.threeMonthEnsembleAvg}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null;
                        })()}
                        <div className="p-4">
                          {formatCSVAsTable(fixIPredictions.content, 'Fix I')}
                        </div>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <p>No Fix I predictions loaded</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Fix II Predictions - Foldable, folded by default */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => setFixIIExpanded(!fixIIExpanded)}
                  className="w-full bg-green-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-green-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <h4 className="font-semibold text-gray-800">Fix II Predictions</h4>
                    {fixIIPredictions && (
                      <span className="text-xs text-gray-600">(Model: {viewerFixIIModel})</span>
                    )}
                  </div>
                  {fixIIExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                </button>

                {fixIIExpanded && (
                  <div>
                    {fixIIPredictions && fixIIPredictions.content ? (
                      <div>
                        {(() => {
                          const fixIIAverages = calculateDailyAverages(fixIIPredictions.content);
                          const fixIAverages = fixIPredictions && fixIPredictions.content ? calculateDailyAverages(fixIPredictions.content) : null;

                          let predictionDiff = null;
                          let fullEnsembleDiff = null;
                          let threeMonthEnsembleDiff = null;

                          if (fixIIAverages && fixIAverages) {
                            const fixIPred = parseFloat(fixIAverages.predictionAvg);
                            const fixIIPred = parseFloat(fixIIAverages.predictionAvg);
                            if (fixIPred !== 0) {
                              predictionDiff = (((fixIIPred - fixIPred) / fixIPred) * 100).toFixed(2);
                            }
                            if (fixIIAverages.fullEnsembleAvg && fixIAverages.fullEnsembleAvg) {
                              const fixIFull = parseFloat(fixIAverages.fullEnsembleAvg);
                              const fixIIFull = parseFloat(fixIIAverages.fullEnsembleAvg);
                              if (fixIFull !== 0) {
                                fullEnsembleDiff = (((fixIIFull - fixIFull) / fixIFull) * 100).toFixed(2);
                              }
                            }
                            if (fixIIAverages.threeMonthEnsembleAvg && fixIAverages.threeMonthEnsembleAvg) {
                              const fixIThree = parseFloat(fixIAverages.threeMonthEnsembleAvg);
                              const fixIIThree = parseFloat(fixIIAverages.threeMonthEnsembleAvg);
                              if (fixIThree !== 0) {
                                threeMonthEnsembleDiff = (((fixIIThree - fixIThree) / fixIThree) * 100).toFixed(2);
                              }
                            }
                          }

                          return fixIIAverages ? (
                            <div className="p-4 bg-gray-50 border-b border-gray-200">
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white rounded p-2 border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Prediction Avg</p>
                                  <div className="flex items-center justify-between">
                                    <p className="text-lg font-bold text-blue-600">{fixIIAverages.predictionAvg}</p>
                                    {predictionDiff !== null && (
                                      <div className={`flex items-center text-sm font-medium ${parseFloat(predictionDiff) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {parseFloat(predictionDiff) >= 0 ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        {Math.abs(parseFloat(predictionDiff))}%
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="bg-white rounded p-2 border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Meteologica Avg</p>
                                  <p className="text-lg font-bold text-gray-600">{fixIIAverages.meteologicaAvg}</p>
                                </div>
                                {fixIIAverages.fullEnsembleAvg && (
                                  <div className="bg-white rounded p-2 border border-gray-200">
                                    <p className="text-xs text-gray-500 uppercase">Full Ensemble</p>
                                    <div className="flex items-center justify-between">
                                      <p className="text-lg font-bold text-purple-600">{fixIIAverages.fullEnsembleAvg}</p>
                                      {fullEnsembleDiff !== null && (
                                        <div className={`flex items-center text-sm font-medium ${parseFloat(fullEnsembleDiff) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {parseFloat(fullEnsembleDiff) >= 0 ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                          {Math.abs(parseFloat(fullEnsembleDiff))}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {fixIIAverages.threeMonthEnsembleAvg && (
                                  <div className="bg-white rounded p-2 border border-gray-200">
                                    <p className="text-xs text-gray-500 uppercase">3-Month Ensemble</p>
                                    <div className="flex items-center justify-between">
                                      <p className="text-lg font-bold text-teal-600">{fixIIAverages.threeMonthEnsembleAvg}</p>
                                      {threeMonthEnsembleDiff !== null && (
                                        <div className={`flex items-center text-sm font-medium ${parseFloat(threeMonthEnsembleDiff) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {parseFloat(threeMonthEnsembleDiff) >= 0 ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                          {Math.abs(parseFloat(threeMonthEnsembleDiff))}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null;
                        })()}
                        <div className="p-4">
                          {formatCSVAsTable(fixIIPredictions.content, 'Fix II')}
                        </div>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <p>No Fix II predictions loaded</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* IDA1 Model Predictions */}
            <div className="border border-gray-200 rounded-lg mt-4">
              <button
                onClick={() => setIda1Expanded(!ida1Expanded)}
                className="w-full bg-orange-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-orange-100 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <h4 className="font-semibold text-gray-800">IDA1 Model Predictions</h4>
                  {ida1Predictions && (
                    <span className="text-xs text-gray-600">(Model: {viewerIda1Model})</span>
                  )}
                </div>
                {ida1Expanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
              </button>

              {ida1Expanded && (
                <div>
                  {ida1Predictions && ida1Predictions.predictions ? (
                    <div>
                      {/* IDA1 Averages */}
                      {(() => {
                        const preds = ida1Predictions.predictions;
                        const hours = Object.keys(preds).map(Number).sort((a, b) => a - b);
                        if (hours.length === 0) return null;

                        const q50Avg = hours.reduce((sum, h) => sum + (preds[h]?.stacked_q50 || 0), 0) / hours.length;

                        return (
                          <div className="p-4 bg-gray-50 border-b border-gray-200">
                            <p className="text-xs text-gray-500 mb-3">
                              Hourly values are averaged from the latest four quarter-hour predictions for each hour.
                            </p>
                            <div className="grid grid-cols-3 gap-3">
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-xs text-gray-500 uppercase">Hourly q50 Avg</p>
                                <p className="text-lg font-bold text-orange-600">{q50Avg.toFixed(2)}</p>
                              </div>
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-xs text-gray-500 uppercase">Hours</p>
                                <p className="text-lg font-bold text-gray-600">{hours.length}</p>
                              </div>
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-xs text-gray-500 uppercase">Model</p>
                                <p className="text-lg font-bold text-gray-600">{viewerIda1Model}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* IDA1 Table */}
                      <div className="p-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q05</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q10</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q20</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q30</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q40</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase font-bold text-gray-700">q50</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q60</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q70</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q80</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q90</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">q95</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {Object.keys(ida1Predictions.predictions).map(Number).sort((a, b) => a - b).map((hour, idx) => {
                              const row = ida1Predictions.predictions[hour];
                              const displayHour = addOneHour ? hour + 1 : hour;
                              return (
                                <tr key={hour} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-orange-50`}>
                                  <td className="px-3 py-1.5 text-sm text-gray-900 font-medium">{displayHour}</td>
                                  {['stacked_q05', 'stacked_q10', 'stacked_q20', 'stacked_q30', 'stacked_q40', 'stacked_q50', 'stacked_q60', 'stacked_q70', 'stacked_q80', 'stacked_q90', 'stacked_q95'].map(q => (
                                    <td key={q} className={`px-3 py-1.5 text-sm ${q === 'stacked_q50' ? 'font-bold text-orange-700' : 'text-gray-900'}`}>
                                      {row[q] != null ? row[q].toFixed(2) : '-'}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <p>No IDA1 predictions loaded</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Models;
